import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import SetUpReminder from '../components/SetUpReminder';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Crown, CheckCircle2, Settings as SettingsIcon, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { CURRENCIES, getCurrencySymbol } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'annually', label: 'Annually' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'six-monthly', label: 'Every 6 months' },
];

export default function AddDebit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [savedDebit, setSavedDebit] = useState(null);
  const dismissKey = user?.id ? `mdd_addtip_dismissed_${user.id}` : null;
  const [tipDismissed, setTipDismissed] = useState(false);
  useEffect(() => {
    if (dismissKey) setTipDismissed(localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);
  const dismissTip = () => {
    if (dismissKey) {
      try { localStorage.setItem(dismissKey, '1'); } catch { /* ignore */ }
    }
    setTipDismissed(true);
  };
  const [formData, setFormData] = useState({
    company_name: '',
    amount: '',
    payment_day: '',
    frequency: 'monthly',
    notes: '',
    currency: 'GBP'
  });

  useEffect(() => {
    checkSubscription();
  }, []);

  useEffect(() => {
    if (user?.default_currency) {
      setFormData(prev => ({ ...prev, currency: user.default_currency }));
    }
  }, [user]);

  const checkSubscription = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscription/status`);
      setSubscription(data);
    } catch (err) {
      console.error('Error checking subscription:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name.trim()) {
      toast.error('Please enter a company name');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!formData.payment_day || parseInt(formData.payment_day) < 1 || parseInt(formData.payment_day) > 31) {
      toast.error('Please enter a valid payment day (1-31)');
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/direct-debits`,
        {
          company_name: formData.company_name.trim(),
          amount: parseFloat(formData.amount),
          payment_day: parseInt(formData.payment_day),
          frequency: formData.frequency,
          notes: formData.notes.trim() || null,
          currency: formData.currency
        },
        
      );

      toast.success('Direct debit added! Now set up your reminder below.');
      setSavedDebit(data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to add direct debit';
      toast.error(message);
      
      // If free tier limit reached, redirect to upgrade
      if (err.response?.status === 403) {
        setTimeout(() => navigate('/upgrade'), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const isPremium = subscription?.tier === 'premium';
  const canAdd = subscription?.can_add_more !== false;

  return (
    <PageLayout showNav showUpgrade isPremium={isPremium}>
      <div className="px-4 py-6 max-w-lg mx-auto">
        <h1 className="font-heading font-bold text-dark text-2xl mb-6">
          Add Direct Debit
        </h1>

        {/* Settings tip — points users to optional fields that unlock the estimated balance feature */}
        {!tipDismissed && (
          <div
            className="mb-5 rounded-2xl p-4 bg-gold-light border-l-4 border-gold relative animate-fade-in"
            data-testid="add-settings-tip"
          >
            <button
              onClick={dismissTip}
              data-testid="add-settings-tip-dismiss"
              className="absolute top-2.5 right-2.5 p-1 text-text-light hover:text-dark transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3 pr-6">
              <div className="w-9 h-9 bg-gold rounded-full flex items-center justify-center flex-shrink-0">
                <SettingsIcon className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-dark text-sm leading-tight mb-1">
                  Tip: more in Settings
                </p>
                <p className="text-xs text-text-secondary mb-2">
                  Set your <strong>default currency</strong>, app language, and add your monthly income, payday and overdraft limit to unlock your <strong>estimated balance</strong>.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/settings')}
                  data-testid="add-settings-tip-link"
                  className="text-primary text-xs font-semibold hover:underline"
                >
                  Open Settings →
                </button>
              </div>
            </div>
          </div>
        )}

        {!canAdd && (
          <div 
            className="card-premium p-5 mb-6 animate-fade-in"
            data-testid="upgrade-prompt"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-dark">Free limit reached</p>
                <p className="text-sm text-text-secondary">
                  You've reached your limit of {subscription?.debit_limit} direct debits
                </p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/upgrade')}
              className="btn-premium w-full py-3"
              data-testid="upgrade-btn"
            >
              Go Premium for Unlimited
            </Button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="card-default p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Company Name
            </label>
            <Input
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              placeholder="e.g. Netflix, Spotify, Gym"
              data-testid="company-name-input"
              className="input-field w-full"
              disabled={!canAdd || !!savedDebit}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Amount
            </label>
            <div className="flex gap-2">
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                disabled={!canAdd || !!savedDebit}
                data-testid="currency-select"
                className="input-field w-24 rounded-xl px-2 py-2 text-sm border border-border bg-white"
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                ))}
              </select>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                data-testid="amount-input"
                className="input-field flex-1"
                disabled={!canAdd || !!savedDebit}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Payment Day (1-31)
            </label>
            <Input
              type="number"
              min="1"
              max="31"
              value={formData.payment_day}
              onChange={(e) => {
                let val = e.target.value;
                if (val && parseInt(val) > 31) val = '31';
                setFormData({ ...formData, payment_day: val });
              }}
              placeholder="15"
              data-testid="payment-day-input"
              className="input-field w-full"
              disabled={!canAdd || !!savedDebit}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Frequency
            </label>
            <div className="flex flex-wrap gap-2" data-testid="frequency-selector">
              {FREQUENCIES.map((freq) => (
                <button
                  key={freq.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, frequency: freq.value })}
                  disabled={!canAdd || !!savedDebit}
                  data-testid={`freq-${freq.value}`}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    formData.frequency === freq.value
                      ? 'bg-primary text-white'
                      : 'bg-primary-light text-primary-dark hover:bg-primary/20'
                  } ${(!canAdd || !!savedDebit) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {freq.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Notes (optional)
            </label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes..."
              data-testid="notes-input"
              className="input-field w-full min-h-[100px]"
              disabled={!canAdd || !!savedDebit}
            />
          </div>

          {!savedDebit && (
            <Button
              type="submit"
              disabled={loading || !canAdd}
              data-testid="add-debit-submit-btn"
              className="btn-primary w-full py-3 text-base"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                'Add Direct Debit'
              )}
            </Button>
          )}
        </form>

        {/* SUCCESS CONFIRMATION — shown after saving */}
        {savedDebit && (
          <>
            <div 
              className="bg-primary/10 border-2 border-primary rounded-2xl p-5 mt-6 animate-fade-in"
              data-testid="debit-saved-confirmation"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-dark text-lg">Saved!</p>
                  <p className="text-sm text-text-secondary">
                    <strong>{savedDebit.company_name}</strong> — {getCurrencySymbol(savedDebit.currency)}{savedDebit.amount.toFixed(2)} {savedDebit.frequency}, day {savedDebit.payment_day}
                  </p>
                </div>
              </div>
            </div>

            <SetUpReminder debit={savedDebit} />
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="go-to-dashboard-btn"
              className="btn-primary w-full py-3 text-base mt-4"
            >
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </PageLayout>
  );
}
